import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Switch,
    TablePagination,
    Grid,
    Card,
    CircularProgress
} from "@mui/material";

import {
    Search,
    CheckCircle,
    CurrencyRupee,
    Groups,
    AccountBalanceWallet,
    Paid
} from "@mui/icons-material";

import {
    collection,
    getDocs,
    doc,
    query,
    where,
    writeBatch,
    increment,
    Timestamp,
    addDoc,
    orderBy
} from "firebase/firestore";

import { db } from "../../firebase";
import { format } from "date-fns";
import Swal from "sweetalert2";

function Staffpayreport() {

    const [staffList, setStaffList] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [advanceData, setAdvanceData] = useState([]);
    const [banks, setBanks] = useState([]);
    const [paidRecords, setPaidRecords] = useState({});

    const [loading, setLoading] = useState(false);

    // FILTERS
    const [fromDate, setFromDate] = useState(
        format(new Date(), "yyyy-MM-01")
    );

    const [toDate, setToDate] = useState(
        format(new Date(), "yyyy-MM-dd")
    );

    const [searchTerm, setSearchTerm] = useState("");

    // PAGINATION
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // SUMMARY
    const [totalSalary, setTotalSalary] = useState(0);
    const [totalAdvance, setTotalAdvance] = useState(0);
    const [totalPaid, setTotalPaid] = useState(0);

    // PAYMENT DIALOG
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const [activeStaff, setActiveStaff] = useState(null);

    const [deductAdvance, setDeductAdvance] = useState(true);

    const [paymentDetails, setPaymentDetails] = useState({
        date: format(new Date(), "yyyy-MM-dd"),
        amount: 0,
        bankId: "",
        payMode: "UPI",
        description: ""
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {

        setLoading(true);

        try {

            // STAFF
            const staffSnap = await getDocs(
                collection(db, "salaryAllocations")
            );

            const staffData = staffSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));

            setStaffList(staffData);

            // ATTENDANCE
            const attendanceSnap = await getDocs(
                collection(db, "staffattendance")
            );

            const filteredAttendance =
                attendanceSnap.docs
                    .map(d => d.data())
                    .filter(a => {
                        return a.date >= fromDate &&
                            a.date <= toDate;
                    });

            setAttendanceData(filteredAttendance);

            // ADVANCES
            const advanceSnap = await getDocs(
                query(
                    collection(db, "staffadvances"),
                    where("status", "==", "Pending")
                )
            );

            const advances =
                advanceSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));

            setAdvanceData(advances);

            // BANKS
            const bankSnap = await getDocs(
                collection(db, "banks")
            );

            setBanks(
                bankSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }))
            );

            // PAYMENTS
            const paymentSnap = await getDocs(
                collection(db, "monthlyPayments")
            );

            const payMap = {};

            paymentSnap.docs.forEach(doc => {

                const data = doc.data();

                if (
                    data.date >= fromDate &&
                    data.date <= toDate
                ) {
                    payMap[data.staffId] = data;
                }
            });

            setPaidRecords(payMap);

            // SUMMARY
            let salaryTotal = 0;
            let advanceTotal = 0;
            let paidTotal = 0;

            staffData.forEach(staff => {

                const stats =
                    calculateStatsFromData(
                        staff,
                        filteredAttendance,
                        advances
                    );

                salaryTotal += stats.earned;
                advanceTotal += stats.pendingAdvanceSum;

                if (payMap[staff.id]) {
                    paidTotal += Number(payMap[staff.id].amount || 0);
                }
            });

            setTotalSalary(salaryTotal);
            setTotalAdvance(advanceTotal);
            setTotalPaid(paidTotal);

        } catch (err) {
            console.log(err);
        }

        setLoading(false);
    };

    const calculateStatsFromData = (
        staff,
        attendance,
        advances
    ) => {

        const logs =
            attendance.filter(a => a.staffId === staff.id);

        const p =
            logs.filter(
                l =>
                    l.status === "Present" ||
                    l.status === "Late Mark"
            ).length;

        const h =
            logs.filter(
                l => l.status === "Half Day"
            ).length;

        const pendingAdvanceSum =
            advances
                .filter(adv => adv.staffId === staff.id)
                .reduce(
                    (sum, item) =>
                        sum + Number(item.payment || 0),
                    0
                );

        const base =
            Number(staff.totalSalary || 0);

        const earned =
            Math.round(
                (p * (base / 30)) +
                (h * 0.5 * (base / 30))
            );

        return {
            p,
            h,
            earned,
            pendingAdvanceSum
        };
    };

    const calculateStats = (staff) => {
        return calculateStatsFromData(
            staff,
            attendanceData,
            advanceData
        );
    };

    const handleFinalPay = async () => {

        if (
            paymentDetails.payMode !== "Cash" &&
            !paymentDetails.bankId
        ) {
            return Swal.fire(
                "Error",
                "Select Bank",
                "error"
            );
        }

        try {

            const batch = writeBatch(db);

            // SAVE PAYMENT
            await addDoc(
                collection(db, "monthlyPayments"),
                {
                    staffId: activeStaff.id,
                    staffName: activeStaff.staffName,
                    fromDate,
                    toDate,
                    ...paymentDetails,
                    advanceDeducted: deductAdvance,
                    createdAt: Timestamp.now()
                }
            );

            // UPDATE ADVANCES
            if (deductAdvance) {

                const staffAdvances =
                    advanceData.filter(
                        a => a.staffId === activeStaff.id
                    );

                staffAdvances.forEach(adv => {

                    const advRef =
                        doc(db, "staffadvances", adv.id);

                    batch.update(advRef, {
                        status: "Paid"
                    });

                });
            }

            // BANK DEDUCTION
            if (paymentDetails.payMode !== "Cash") {

                const bankRef =
                    doc(
                        db,
                        "banks",
                        paymentDetails.bankId
                    );

                batch.update(bankRef, {
                    balance: increment(
                        -Number(paymentDetails.amount)
                    )
                });

            }

            await batch.commit();

            Swal.fire(
                "Success",
                "Salary Paid Successfully",
                "success"
            );

            setPayDialogOpen(false);

            loadData();

        } catch (err) {
            Swal.fire(
                "Error",
                err.message,
                "error"
            );
        }
    };

    const filteredStaff =
        staffList.filter(s =>
            s.staffName
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
        );

    return (
        <Box p={2} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>

            {/* HEADER */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>

                <Stack
                    direction={{
                        xs: "column",
                        md: "row"
                    }}
                    justifyContent="space-between"
                    spacing={2}
                >

                    <Box>
                        <Typography
                            variant="h5"
                            fontWeight="bold"
                        >
                            Staff Salary Report
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Date wise salary payment report
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={2}
                    >

                        <TextField
                            type="date"
                            size="small"
                            label="From Date"
                            value={fromDate}
                            onChange={(e) =>
                                setFromDate(e.target.value)
                            }
                            InputLabelProps={{
                                shrink: true
                            }}
                        />

                        <TextField
                            type="date"
                            size="small"
                            label="To Date"
                            value={toDate}
                            onChange={(e) =>
                                setToDate(e.target.value)
                            }
                            InputLabelProps={{
                                shrink: true
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={loadData}
                        >
                            Get Report
                        </Button>

                    </Stack>

                </Stack>

            </Paper>

            {/* SUMMARY */}
            <Grid container spacing={2} mb={3}>

                <Grid item xs={12} md={4}>
                    <Card sx={{
                        p: 2,
                        textAlign: "center"
                    }}>
                        <CurrencyRupee
                            color="primary"
                        />

                        <Typography
                            variant="h4"
                            fontWeight="bold"
                        >
                            ₹{totalSalary}
                        </Typography>

                        <Typography>
                            Total Salary
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{
                        p: 2,
                        textAlign: "center"
                    }}>
                        <AccountBalanceWallet
                            color="warning"
                        />

                        <Typography
                            variant="h4"
                            fontWeight="bold"
                        >
                            ₹{totalAdvance}
                        </Typography>

                        <Typography>
                            Pending Advance
                        </Typography>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{
                        p: 2,
                        textAlign: "center"
                    }}>
                        <Paid color="success" />

                        <Typography
                            variant="h4"
                            fontWeight="bold"
                        >
                            ₹{totalPaid}
                        </Typography>

                        <Typography>
                            Paid Amount
                        </Typography>
                    </Card>
                </Grid>

            </Grid>

            {/* SEARCH */}
            <Paper sx={{ p: 2, mb: 2 }}>

                <TextField
                    fullWidth
                    placeholder="Search Staff..."
                    size="small"
                    onChange={(e) =>
                        setSearchTerm(e.target.value)
                    }
                    InputProps={{
                        startAdornment:
                            <Search
                                sx={{
                                    mr: 1,
                                    color: "gray"
                                }}
                            />
                    }}
                />

            </Paper>

            {/* TABLE */}
            <TableContainer
                component={Paper}
                sx={{ borderRadius: 3 }}
            >

                <Table size="small">

                    <TableHead sx={{ bgcolor: "#f1f5f9" }}>

                        <TableRow>

                            <TableCell>
                                <b>Sr No</b>
                            </TableCell>

                            <TableCell>
                                <b>Staff</b>
                            </TableCell>

                            <TableCell align="center">
                                <b>Attendance</b>
                            </TableCell>

                            <TableCell align="center">
                                <b>Earned</b>
                            </TableCell>

                            <TableCell align="center">
                                <b>Advance</b>
                            </TableCell>

                            <TableCell align="center">
                                <b>Status</b>
                            </TableCell>

                            <TableCell align="right">
                                <b>Action</b>
                            </TableCell>

                        </TableRow>

                    </TableHead>

                    <TableBody>

                        {loading ? (

                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    align="center"
                                >
                                    <CircularProgress
                                        sx={{ my: 4 }}
                                    />
                                </TableCell>
                            </TableRow>

                        ) : filteredStaff.length === 0 ? (

                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    align="center"
                                >
                                    No Records Found
                                </TableCell>
                            </TableRow>

                        ) : (

                            filteredStaff
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage +
                                    rowsPerPage
                                )
                                .map((staff, index) => {

                                    const {
                                        p,
                                        h,
                                        earned,
                                        pendingAdvanceSum
                                    } = calculateStats(staff);

                                    const paymentRecord =
                                        paidRecords[staff.id];

                                    const isPaid =
                                        !!paymentRecord;

                                    return (

                                        <TableRow
                                            key={staff.id}
                                            hover
                                        >

                                            <TableCell>
                                                {
                                                    page *
                                                    rowsPerPage +
                                                    index +
                                                    1
                                                }
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    fontWeight="bold"
                                                >
                                                    {
                                                        staff.staffName
                                                    }
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                >
                                                    Salary :
                                                    ₹{
                                                        staff.totalSalary
                                                    }
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Chip
                                                    label={`${p} P | ${h} H`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>

                                            <TableCell align="center">
                                                ₹{earned}
                                            </TableCell>

                                            <TableCell align="center">
                                                <Typography
                                                    color={
                                                        pendingAdvanceSum > 0
                                                            ? "error"
                                                            : "inherit"
                                                    }
                                                    fontWeight="bold"
                                                >
                                                    ₹{
                                                        pendingAdvanceSum
                                                    }
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center">

                                                {
                                                    isPaid ? (

                                                        <Chip
                                                            icon={
                                                                <CheckCircle />
                                                            }
                                                            label="Paid"
                                                            color="success"
                                                            size="small"
                                                        />

                                                    ) : (

                                                        <Chip
                                                            label="Unpaid"
                                                            size="small"
                                                            variant="outlined"
                                                        />

                                                    )
                                                }

                                            </TableCell>

                                            <TableCell align="right">

                                                {
                                                    isPaid ? (

                                                        <Typography
                                                            fontWeight="bold"
                                                        >
                                                            ₹{
                                                                paymentRecord.amount
                                                            }
                                                        </Typography>

                                                    ) : null
                                                }

                                            </TableCell>

                                        </TableRow>

                                    );

                                })

                        )}

                    </TableBody>

                </Table>

                <TablePagination
                    component="div"
                    count={filteredStaff.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        50
                    ]}
                    onPageChange={(e, newPage) =>
                        setPage(newPage)
                    }
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(
                            parseInt(e.target.value, 10)
                        );
                        setPage(0);
                    }}
                />

            </TableContainer>

            {/* PAYMENT DIALOG */}
            <Dialog
                open={payDialogOpen}
                onClose={() =>
                    setPayDialogOpen(false)
                }
                fullWidth
                maxWidth="sm"
            >

                <DialogTitle>
                    Salary Payment
                </DialogTitle>

                <DialogContent dividers>

                    <Stack spacing={2} mt={1}>

                        <Typography
                            fontWeight="bold"
                        >
                            {activeStaff?.staffName}
                        </Typography>

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                            bgcolor="#f1f5f9"
                            borderRadius={2}
                        >

                            <Typography>
                                Deduct Advance
                            </Typography>

                            <Switch
                                checked={deductAdvance}
                                onChange={(e) => {

                                    setDeductAdvance(
                                        e.target.checked
                                    );

                                    const {
                                        earned,
                                        pendingAdvanceSum
                                    } =
                                        calculateStats(
                                            activeStaff
                                        );

                                    setPaymentDetails({
                                        ...paymentDetails,
                                        amount:
                                            e.target.checked
                                                ? (
                                                    earned -
                                                    pendingAdvanceSum
                                                )
                                                : earned
                                    });

                                }}
                            />

                        </Box>

                        <TextField
                            label="Pay Amount"
                            type="number"
                            fullWidth
                            value={paymentDetails.amount}
                            onChange={(e) =>
                                setPaymentDetails({
                                    ...paymentDetails,
                                    amount:
                                        e.target.value
                                })
                            }
                        />

                        <TextField
                            select
                            label="Pay Mode"
                            fullWidth
                            value={paymentDetails.payMode}
                            onChange={(e) =>
                                setPaymentDetails({
                                    ...paymentDetails,
                                    payMode:
                                        e.target.value
                                })
                            }
                        >

                            {
                                [
                                    "Cash",
                                    "UPI",
                                    "Bank Transfer"
                                ].map(mode => (
                                    <MenuItem
                                        key={mode}
                                        value={mode}
                                    >
                                        {mode}
                                    </MenuItem>
                                ))
                            }

                        </TextField>

                        {
                            paymentDetails.payMode !== "Cash" && (

                                <TextField
                                    select
                                    label="Select Bank"
                                    fullWidth
                                    value={paymentDetails.bankId}
                                    onChange={(e) =>
                                        setPaymentDetails({
                                            ...paymentDetails,
                                            bankId:
                                                e.target.value
                                        })
                                    }
                                >

                                    {
                                        banks.map(bank => (

                                            <MenuItem
                                                key={bank.id}
                                                value={bank.id}
                                            >
                                                {bank.bankname}
                                            </MenuItem>

                                        ))
                                    }

                                </TextField>

                            )
                        }

                    </Stack>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setPayDialogOpen(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={handleFinalPay}
                    >
                        Confirm Payment
                    </Button>

                </DialogActions>

            </Dialog>

        </Box>
    );
}

export default Staffpayreport;